import { useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft, CreditCard, Shield, Check, ArrowRight } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';

const plans: Record<string, { name: string; price: string; priceNum: number; description: string; features: string[] }> = {
  basic: {
    name: 'Basic Website Build',
    price: '£500',
    priceNum: 50000,
    description: 'Single-page custom website',
    features: [
      '1 page custom design',
      'Mobile responsive',
      'Contact form integration',
      '2 revision rounds',
      'Full source code',
    ],
  },
  standard: {
    name: 'Standard Website Build',
    price: '£1,500',
    priceNum: 150000,
    description: 'Multi-page site for businesses ready to scale',
    features: [
      'Up to 5 pages',
      'Mobile responsive',
      'Contact form integration',
      'CMS setup',
      '3 revision rounds',
      'Full source code',
    ],
  },
  premium: {
    name: 'Premium Website Build',
    price: '£3,000',
    priceNum: 300000,
    description: 'Comprehensive site with advanced features',
    features: [
      'Up to 10 pages',
      'Custom animations',
      'CMS integration',
      'Blog setup',
      'Performance optimization',
      '5 revision rounds',
      'Full source code',
    ],
  },
  starter: {
    name: 'Starter Monthly',
    price: '£99',
    priceNum: 9900,
    description: 'Essential support to keep your site running smoothly',
    features: [
      'Hosting & SSL',
      'Monthly backups',
      'Security updates',
      'Content changes (2/month)',
      'Email support',
      'Uptime monitoring',
    ],
  },
  professional: {
    name: 'Professional Monthly',
    price: '£249',
    priceNum: 24900,
    description: 'Growth-focused support with more flexibility',
    features: [
      'Everything in Starter',
      'Priority hosting',
      'Weekly backups',
      'Content changes (10/month)',
      'Performance reports',
      'Minor design tweaks',
      'Phone & email support',
      'Quarterly review call',
    ],
  },
  enterprise: {
    name: 'Enterprise Monthly',
    price: '£499',
    priceNum: 49900,
    description: 'Full-service partnership for ambitious businesses',
    features: [
      'Everything in Professional',
      'Dedicated account manager',
      'Daily backups',
      'Unlimited content changes',
      'A/B testing setup',
      'Analytics dashboard',
      'New page builds (2/month)',
      '24/7 priority support',
    ],
  },
};

const monthlyAddons = [
  {
    id: 'starter',
    name: 'Starter Support',
    price: '£99',
    priceNum: 9900,
    period: '/month',
    description: 'Essential support to keep your site running smoothly',
    features: [
      'Hosting & SSL',
      'Monthly backups',
      'Security updates',
      'Content changes (2/month)',
      'Email support',
      'Uptime monitoring',
    ],
  },
  {
    id: 'professional',
    name: 'Professional Support',
    price: '£249',
    priceNum: 24900,
    period: '/month',
    description: 'Growth-focused support with more flexibility',
    features: [
      'Everything in Starter',
      'Priority hosting',
      'Weekly backups',
      'Content changes (10/month)',
      'Performance reports',
      'Minor design tweaks',
      'Phone & email support',
      'Quarterly review call',
    ],
  },
  {
    id: 'enterprise',
    name: 'Enterprise Support',
    price: '£499',
    priceNum: 49900,
    period: '/month',
    description: 'Full-service partnership for ambitious businesses',
    features: [
      'Everything in Professional',
      'Dedicated account manager',
      'Daily backups',
      'Unlimited content changes',
      'A/B testing setup',
      'Analytics dashboard',
      'New page builds (2/month)',
      '24/7 priority support',
    ],
  },
];

type Step = 'details' | 'addons' | 'success';

const Checkout = () => {
  const [searchParams] = useSearchParams();
  const planId = searchParams.get('plan') || 'standard';
  const plan = plans[planId] || plans.standard;
  const isBuildPlan = ['basic', 'standard', 'premium'].includes(planId);

  const [step, setStep] = useState<Step>('details');
  const [selectedAddon, setSelectedAddon] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleDetailsSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (isBuildPlan) {
      setStep('addons');
    } else {
      handleCheckout();
    }
  };

  const handleCheckout = async (addonId?: string | null) => {
    setIsSubmitting(true);
    try {
      const response = await fetch('/api/create-checkout-session', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          planId,
          addonId: addonId || selectedAddon,
          ...formData,
        }),
      });
      const data = await response.json();
      if (data.url) {
        window.location.href = data.url;
      } else {
        setStep('success');
      }
    } catch (error) {
      console.error('Error:', error);
      setStep('success');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSkipAddon = () => {
    handleCheckout(null);
  };

  if (step === 'success') {
    return (
      <div className="min-h-screen bg-white dark:bg-dark pt-24 pb-12 px-6">
        <div className="max-w-xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5 }}
          >
            <div className="w-20 h-20 bg-green-100 dark:bg-green-900 rounded-full flex items-center justify-center mx-auto mb-6">
              <Check className="text-green-600 dark:text-green-400" size={40} />
            </div>
            <h1 className="text-3xl font-bold text-primary dark:text-white mb-4">Thank You!</h1>
            <p className="text-gray-600 dark:text-gray-400 mb-2">
              Your enquiry for <strong>{plan.name}</strong> has been received.
            </p>
            <p className="text-gray-600 dark:text-gray-400 mb-8">
              We'll be in touch within 24 hours to discuss your project.
            </p>
            <a
              href="/"
              className="inline-flex items-center gap-2 px-6 py-3 bg-primary text-white rounded-xl hover:bg-secondary transition-colors"
            >
              <ArrowLeft size={18} />
              Back to Home
            </a>
          </motion.div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white dark:bg-dark pt-24 pb-12">
      <div className="max-w-6xl mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <a
            href="/"
            className="inline-flex items-center gap-2 text-gray-500 dark:text-gray-400 hover:text-primary dark:hover:text-white transition-colors mb-8"
          >
            <ArrowLeft size={18} />
            Back to Pricing
          </a>

          {/* Progress Steps */}
          <div className="flex items-center gap-4 mb-8">
            <div className={`flex items-center gap-2 ${step === 'details' ? 'text-primary font-semibold' : 'text-gray-400'}`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm ${step === 'details' ? 'bg-primary text-white' : 'bg-gray-200 dark:bg-gray-700 text-gray-500'}`}>1</div>
              <span>Your Details</span>
            </div>
            {isBuildPlan && (
              <>
                <div className="flex-1 h-px bg-gray-200 dark:bg-gray-700" />
                <div className={`flex items-center gap-2 ${step === 'addons' ? 'text-primary font-semibold' : 'text-gray-400'}`}>
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm ${step === 'addons' ? 'bg-primary text-white' : 'bg-gray-200 dark:bg-gray-700 text-gray-500'}`}>2</div>
                  <span>Support Plan</span>
                </div>
              </>
            )}
          </div>

          {step === 'details' ? (
            <div className="grid md:grid-cols-2 gap-12">
              {/* Left: Plan Summary */}
              <div>
                <h1 className="text-3xl font-bold text-primary dark:text-white mb-2">Checkout</h1>
                <p className="text-gray-500 dark:text-gray-400 mb-8">Complete your enquiry below</p>

                <div className="bg-gray-50 dark:bg-secondary/50 rounded-2xl p-6 mb-6">
                  <h2 className="text-xl font-bold text-primary dark:text-white mb-2">{plan.name}</h2>
                  <p className="text-gray-500 dark:text-gray-400 text-sm mb-4">{plan.description}</p>
                  <div className="text-3xl font-bold text-highlight mb-4">{plan.price}</div>
                  <ul className="space-y-2">
                    {plan.features.map((feature) => (
                      <li key={feature} className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                        <Check size={16} className="text-green-500" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
                  <Shield size={16} />
                  <span>Secure checkout. Mockup preview before any payment.</span>
                </div>
              </div>

              {/* Right: Form */}
              <div className="bg-gray-50 dark:bg-secondary/50 rounded-2xl p-8">
                <h2 className="text-xl font-bold text-primary dark:text-white mb-6">Your Details</h2>
                <form onSubmit={handleDetailsSubmit} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Full Name *</label>
                    <input
                      type="text"
                      required
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="w-full px-4 py-3 border border-gray-200 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-primary focus:border-transparent outline-none transition-all bg-white dark:bg-dark"
                      placeholder="Your name"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Email *</label>
                    <input
                      type="email"
                      required
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="w-full px-4 py-3 border border-gray-200 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-primary focus:border-transparent outline-none transition-all bg-white dark:bg-dark"
                      placeholder="your@email.com"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Company</label>
                    <input
                      type="text"
                      value={formData.company}
                      onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                      className="w-full px-4 py-3 border border-gray-200 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-primary focus:border-transparent outline-none transition-all bg-white dark:bg-dark"
                      placeholder="Your company (optional)"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Phone</label>
                    <input
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      className="w-full px-4 py-3 border border-gray-200 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-primary focus:border-transparent outline-none transition-all bg-white dark:bg-dark"
                      placeholder="+44 1234 567890"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Project Details</label>
                    <textarea
                      rows={4}
                      value={formData.message}
                      onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                      className="w-full px-4 py-3 border border-gray-200 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-primary focus:border-transparent outline-none transition-all resize-none bg-white dark:bg-dark"
                      placeholder="Tell us about your project..."
                    />
                  </div>

                  <button
                    type="submit"
                    className="w-full py-4 bg-primary text-white font-semibold rounded-xl hover:bg-secondary transition-all flex items-center justify-center gap-2"
                  >
                    {isBuildPlan ? (
                      <>
                        Continue
                        <ArrowRight size={18} />
                      </>
                    ) : (
                      <>
                        <CreditCard size={18} />
                        Enquire Now — {plan.price}
                      </>
                    )}
                  </button>

                  <p className="text-xs text-gray-500 dark:text-gray-400 text-center">
                    No payment taken today. We'll show you a mockup first.
                  </p>
                </form>
              </div>
            </div>
          ) : (
            /* Addons Step */
            <div className="max-w-4xl mx-auto">
              <div className="text-center mb-12">
                <h1 className="text-3xl font-bold text-primary dark:text-white mb-4">
                  Keep Your Site Running Smoothly
                </h1>
                <p className="text-gray-600 dark:text-gray-400 text-lg max-w-2xl mx-auto">
                  Your website is being built. Add a monthly support plan to keep it fast, secure, and up-to-date.
                </p>
              </div>

              {/* Selected Build Summary */}
              <div className="bg-gray-50 dark:bg-secondary/50 rounded-2xl p-6 mb-8 flex items-center justify-between">
                <div>
                  <h3 className="font-semibold text-primary dark:text-white">{plan.name}</h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400">To be paid now</p>
                </div>
                <div className="text-xl font-bold text-highlight">{plan.price}</div>
              </div>

              {/* Monthly Options */}
              <div className="grid md:grid-cols-3 gap-6 mb-8">
                {monthlyAddons.map((addon) => (
                  <motion.div
                    key={addon.id}
                    whileHover={{ scale: 1.02 }}
                    onClick={() => setSelectedAddon(selectedAddon === addon.id ? null : addon.id)}
                    className={`cursor-pointer rounded-2xl p-6 border-2 transition-all ${
                      selectedAddon === addon.id
                        ? 'border-highlight bg-highlight/5 dark:bg-highlight/10'
                        : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                    }`}
                  >
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="font-bold text-primary dark:text-white">{addon.name}</h3>
                      {selectedAddon === addon.id && (
                        <div className="w-6 h-6 bg-highlight rounded-full flex items-center justify-center">
                          <Check size={14} className="text-white" />
                        </div>
                      )}
                    </div>
                    <div className="mb-4">
                      <span className="text-2xl font-bold text-primary dark:text-white">{addon.price}</span>
                      <span className="text-sm text-gray-500 dark:text-gray-400">{addon.period}</span>
                    </div>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">{addon.description}</p>
                    <ul className="space-y-2">
                      {addon.features.slice(0, 4).map((feature) => (
                        <li key={feature} className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                          <Check size={14} className="text-green-500" />
                          {feature}
                        </li>
                      ))}
                    </ul>
                  </motion.div>
                ))}
              </div>

              {/* Actions */}
              <div className="flex flex-col sm:flex-row gap-4">
                <button
                  onClick={handleSkipAddon}
                  disabled={isSubmitting}
                  className="flex-1 py-4 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 font-semibold rounded-xl hover:bg-gray-100 dark:hover:bg-gray-800 transition-all disabled:opacity-50"
                >
                  {isSubmitting ? 'Processing...' : 'Skip — Just the Build'}
                </button>
                <button
                  onClick={() => handleCheckout()}
                  disabled={!selectedAddon || isSubmitting}
                  className="flex-1 py-4 bg-primary text-white font-semibold rounded-xl hover:bg-secondary transition-all flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <CreditCard size={18} />
                  {isSubmitting ? 'Processing...' : `Pay ${plan.price} Now`}
                </button>
              </div>

              <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400 justify-center mt-6">
                <Shield size={16} />
                <span>Secure checkout. You can add support later anytime.</span>
              </div>
            </div>
          )}
        </motion.div>
      </div>
    </div>
  );
};

export default Checkout;
